import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import {
  FiBell,
  FiCheck,
  FiTrash2,
  FiSettings,
  FiExternalLink,
} from "react-icons/fi";
import { useAuth } from "../../../services/useAuth";
import { isNotificationAdmin, hasPageAccessForUser } from "../../../config/roles";
import { Dropdown, DropdownContent } from "../../BaseUI/Dropdown";
import IconButton from "../../BaseUI/Button/IconButton";
import Button from "../../BaseUI/Button";
import Lozenge from "../../BaseUI/Lozenge";
import { ToastContainer, Toast } from "../Toast";
import {
  API_URL,
  getSeverityLabel,
  SEVERITY_LOZENGE,
  timeAgo,
  decodeNotificationText,
  getNotificationLink,
  getNotificationNavigation,
} from "../../../views/pages/Notifications/constants";
import styles from "./NotificationBell.module.scss";

const NotificationBell = () => {
  const { user, getToken } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [toasts, setToasts] = useState([]);
  const [dropdownWidth, setDropdownWidth] = useState("360px");
  const toastedIdsRef = useRef(new Set());
  const primedRef = useRef(false);

  useEffect(() => {
    const updateWidth = () => {
      const w = Math.min(360, Math.max(280, window.innerWidth - 16));
      setDropdownWidth(`${w}px`);
    };
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  const authHeaders = useCallback(
    () => ({ headers: { Authorization: `Bearer ${getToken()}` } }),
    [getToken],
  );

  const pushToast = useCallback((notification) => {
    const notificationId = notification?._id ? String(notification._id) : "";
    if (notificationId) {
      if (toastedIdsRef.current.has(notificationId)) return;
      toastedIdsRef.current.add(notificationId);
    }

    const id = `${notificationId || "toast"}-${Date.now()}`;
    let appearance = "success";
    if (notification?.severity === "warning") appearance = "warning";
    if (notification?.severity === "critical") appearance = "danger";

    setToasts((prev) => [
      ...prev,
      {
        id,
        title: decodeNotificationText(notification?.title),
        body: decodeNotificationText(notification?.body),
        appearance,
      },
    ]);
    setTimeout(
      () => setToasts((prev) => prev.filter((t) => t.id !== id)),
      6000,
    );
  }, []);

  const fetchNotifications = useCallback(
    async ({ toastNew = false } = {}) => {
      if (!user) return;
      try {
        const { data } = await axios.get(
          `${API_URL}/api/notifications?limit=10`,
          authHeaders(),
        );
        if (!data.status) return;
        const list = data.notifications || [];
        setNotifications(list);
        setUnreadCount(data.unreadCount || 0);

        // The first load only records what the user already has, so reopening the
        // panel never replays old notifications as toasts.
        if (!primedRef.current || !toastNew) {
          list.forEach((item) => toastedIdsRef.current.add(String(item._id)));
          primedRef.current = true;
          return;
        }

        list
          .filter((item) => !item.isRead)
          .slice()
          .reverse()
          .forEach(pushToast);
      } catch {
        /* silent */
      }
    },
    [user, authHeaders, pushToast],
  );

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    if (!user) return;
    const token = getToken();
    if (!token) return;

    const wsUrl = `${API_URL.replace(/^http/, "ws")}/ws?token=${token}`;
    let socket = null;
    let retryTimer = null;
    let retryDelay = 2000;
    let disposed = false;

    const connect = () => {
      socket = new WebSocket(wsUrl);

      socket.onopen = () => {
        retryDelay = 2000;
      };

      socket.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type !== "notification") return;
          setNotifications((prev) => [msg.notification, ...prev.slice(0, 9)]);
          setUnreadCount((c) => c + 1);
          pushToast(msg.notification);
        } catch {
          /* silent */
        }
      };

      // The socket dies on every backend restart or idle proxy timeout, so it has
      // to come back on its own or live notifications stop for the whole session.
      socket.onclose = () => {
        if (disposed) return;
        retryTimer = setTimeout(connect, retryDelay);
        retryDelay = Math.min(retryDelay * 2, 30000);
      };
    };

    connect();

    // Fallback for a socket that never reconnects (offline tab, blocked upgrade).
    const pollTimer = setInterval(() => {
      if (socket?.readyState !== WebSocket.OPEN) {
        fetchNotifications({ toastNew: true });
      }
    }, 30000);

    return () => {
      disposed = true;
      clearInterval(pollTimer);
      if (retryTimer) clearTimeout(retryTimer);
      if (socket) {
        socket.onclose = null;
        socket.close();
      }
    };
  }, [user, getToken, pushToast, fetchNotifications]);

  const markRead = async (id) => {
    try {
      await axios.put(
        `${API_URL}/api/notifications/${id}/read`,
        {},
        authHeaders(),
      );
      setNotifications((prev) =>
        prev.map((n) => (n._id === id ? { ...n, isRead: true } : n)),
      );
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch {
      /* silent */
    }
  };

  const markAllRead = async () => {
    try {
      await axios.put(
        `${API_URL}/api/notifications/read-all`,
        {},
        authHeaders(),
      );
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch {
      /* silent */
    }
  };

  const deleteNotification = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/notifications/${id}`, authHeaders());
      setNotifications((prev) => prev.filter((n) => n._id !== id));
    } catch {
      /* silent */
    }
  };

  const openNotification = async (notification) => {
    const nav = getNotificationNavigation(notification);
    if (!nav) return;
    if (user && !hasPageAccessForUser(user, nav.pathname)) {
      pushToast({
        title: "Erişim yok",
        body: "Bu sayfaya erişim yetkiniz bulunmuyor.",
        severity: "critical",
      });
      return;
    }
    if (!notification.isRead) await markRead(notification._id);
    navigate(nav.pathname, nav.state ? { state: nav.state } : undefined);
  };

  return (
    <div className={styles["bell-container"]}>
      <Dropdown
        trigger={
          <div className={styles["bell-trigger"]} onClick={fetchNotifications}>
            <div className={styles["bell-icon-wrapper"]}>
              <FiBell className={styles["bell-icon"]} />
              {unreadCount > 0 && (
                <span className={styles["bell-badge"]}>
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </div>
          </div>
        }
        placement="bottom-end"
        hideDropdownIcon
        width={dropdownWidth}
        maxHeight="420px"
        closeOnSelect={false}
        customTrigger
      >
        <DropdownContent className={styles["bell-dropdown"]}>
          <div className={styles["bell-header"]}>
            <span className="weight-500">Bildirimler</span>
            <div className="d-flex align-items-center gap-2">
              {unreadCount > 0 && (
                <Button appearance="subtle" onClick={markAllRead}>
                  <FiCheck size={14} /> Tümünü okundu işaretle
                </Button>
              )}
              {isNotificationAdmin(user) && (
                <IconButton
                  icon={<FiSettings />}
                  label="Bildirim ayarları"
                  appearance="subtle"
                  shape="circle"
                  onClick={() => navigate("/settings/notifications")}
                />
              )}
            </div>
          </div>

          <div className={styles["bell-list"]}>
            {notifications.length === 0 ? (
              <div className={styles["bell-empty"]}>Henüz bildirim yok</div>
            ) : (
              notifications.map((n) => (
                <div
                  key={n._id}
                  className={`${styles["bell-item"]} ${!n.isRead ? styles["bell-item--unread"] : ""}`}
                >
                  <div
                    className={styles["bell-item__main"]}
                    role={getNotificationLink(n) ? "link" : undefined}
                    tabIndex={getNotificationLink(n) ? 0 : undefined}
                    style={
                      getNotificationLink(n) ? { cursor: "pointer" } : undefined
                    }
                    onClick={() => openNotification(n)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") openNotification(n);
                    }}
                  >
                    <div className={styles["title-row"]}>
                      <strong className="small">
                        {decodeNotificationText(n.title)}
                      </strong>
                      <div className={styles["lozenge-wrapper"]}>
                        <Lozenge
                          appearance={
                            SEVERITY_LOZENGE[n.severity] || "default-subtle"
                          }
                          isBold
                        >
                          {getSeverityLabel(n.severity)}
                        </Lozenge>
                      </div>
                    </div>
                    <p className="text-muted small mb-1">
                      {decodeNotificationText(n.body)}
                    </p>
                    <span className="text-muted" style={{ fontSize: "11px" }}>
                      {timeAgo(n.createdAt)}
                    </span>
                  </div>
                  <div className={styles["bell-item__actions"]}>
                    {!n.isRead && (
                      <IconButton
                        icon={<FiCheck />}
                        label="Okundu"
                        appearance="subtle"
                        shape="circle"
                        onClick={(event) => {
                          event.stopPropagation();
                          markRead(n._id);
                        }}
                      />
                    )}
                    <IconButton
                      icon={<FiTrash2 />}
                      label="Sil"
                      appearance="subtle"
                      shape="circle"
                      onClick={(event) => {
                        event.stopPropagation();
                        deleteNotification(n._id);
                      }}
                    />
                  </div>
                </div>
              ))
            )}
          </div>

          <div className={styles["bell-footer"]}>
            <Link to="/notifications" className={styles["bell-footer__link"]}>
              <FiExternalLink size={14} /> Tüm bildirimleri gör
            </Link>
          </div>
        </DropdownContent>
      </Dropdown>

      <ToastContainer>
        {toasts.map((t) => (
          <Toast
            key={t.id}
            title={t.title}
            description={t.body}
            appearance={t.appearance}
            onClose={() => setToasts((p) => p.filter((x) => x.id !== t.id))}
          />
        ))}
      </ToastContainer>
    </div>
  );
};

export default NotificationBell;
